<!-- No <admin-nav> of its own: the admin shell (admin.component.html) renders
     the legacy link bar when the old navigation is on, and the rail's admin
     menu replaces it under the new one. A page-level copy showed the bar a
     second time / under the new navigation. -->
<div class="settings">
  <div class="settings__container">
    <div class="settings__header">
      <h1>{{ settings.clientName || 'Client Settings' }}</h1>
      <div class="settings__header-meta" *ngIf="!loading && !loadError">
        <label class="settings__header-label" for="censusHour">Census Report</label>
        <input id="censusHour" type="text" class="settings__input settings__input--time"
               [class.settings__input--invalid]="!isCensusHourValid"
               [(ngModel)]="settings.censusReportHour" placeholder="HH:MM"
               pattern="^([01]\d|2[0-3]):[0-5]\d$" maxlength="5" inputmode="numeric"
               aria-describedby="censusHourError" #censusHourInput="ngModel" />
        <small *ngIf="!isCensusHourValid" id="censusHourError"
               class="settings__field-error" role="alert" aria-live="assertive">
          Census time must be HH:MM (24-hour).
        </small>
      </div>
    </div>

    <div *ngIf="loading" class="settings__loader"></div>

    <div *ngIf="loadError && !loading" class="settings__error" role="alert" aria-live="assertive">
      Failed to load settings. Please refresh the page.
    </div>

    <div *ngIf="!loading && !loadError" class="settings__content">

      <div class="settings__card">
        <div class="settings__card-header">
          <span class="settings__icon settings__icon--general"></span>
          <span>General</span>
        </div>

        <div class="settings__row">
          <label class="settings__label" for="showUnassigned">Show Unassigned Facilities</label>
          <label class="toggle">
            <input id="showUnassigned" type="checkbox" [(ngModel)]="settings.showUnassignedFacilities" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <div class="settings__row">
          <label class="settings__label" for="approvedAdmission">Approved Admission</label>
          <label class="toggle">
            <input id="approvedAdmission" type="checkbox" [(ngModel)]="settings.approvedAdmission" />
            <span class="toggle__track"></span>
          </label>
        </div>
      </div>

      <div class="settings__card">
        <div class="settings__card-header">
          <span class="settings__icon settings__icon--features"></span>
          <span>Features</span>
        </div>

        <div class="settings__row">
          <label class="settings__label" for="aiOutlook">AI Admission Outlook</label>
          <label class="toggle">
            <input id="aiOutlook" type="checkbox" [(ngModel)]="settings.showAiAdmissionOutlook" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <div class="settings__row">
          <label class="settings__label" for="medicare">Medicare Eligibility</label>
          <label class="toggle">
            <input id="medicare" type="checkbox" [(ngModel)]="settings.showMedicareEligibility" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <div class="settings__row">
          <label class="settings__label" for="newPatient">New Patient Info</label>
          <label class="toggle">
            <input id="newPatient" type="checkbox" [(ngModel)]="settings.useNewPatientInfo" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <!-- A paid option, opt-in like RiskAI: off for every client until the
             SNF AI admin switches it on here. On grants the Discharge Plan
             screen and its nav entry, the calendar marks on the bed board, and
             the scheduled Discharge Plan email. -->
        <div class="settings__row">
          <label class="settings__label" for="dischargePlan">Discharge Plan</label>
          <label class="toggle">
            <input id="dischargePlan" type="checkbox" [(ngModel)]="settings.uiSettings.dischargePlanEnabled" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <!-- Same shape: off until switched on here. On shows the Scheduled
             Emails page under Admin - that page only; the schedule editors on
             the report pages and the scheduled runs are not affected. -->
        <div class="settings__row">
          <label class="settings__label" for="scheduledEmails">Scheduled Emails</label>
          <label class="toggle">
            <input id="scheduledEmails" type="checkbox" [(ngModel)]="settings.uiSettings.scheduledEmailsEnabled" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <!-- Same shape: off until switched on here. On offers the MDS Regional and
             MDS Nurse roles on the Users page. Off again keeps existing users on them. -->
        <div class="settings__row">
          <label class="settings__label" for="mdsRoles">MDS Roles</label>
          <label class="toggle">
            <input id="mdsRoles" type="checkbox" [(ngModel)]="settings.uiSettings.mdsRolesEnabled" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <div class="settings__row">
          <span class="settings__label-group">
            <label class="settings__label" for="messagesVisibleToAllFacilities">Messages visible to all facilities</label>
            <span class="settings__help" tabindex="0" aria-label="About messages visible to all facilities"
                  #messagesTip="nbTooltip"
                  nbTooltip="On: new messages default to visible to all facilities. Off (default): new messages default to the author's own facilities. The author can change it per message."
                  nbTooltipPlacement="top" nbTooltipClass="settings-tip"
                  (focus)="messagesTip.show()" (blur)="messagesTip.hide()" (keydown.escape)="messagesTip.hide()">?</span>
          </span>
          <label class="toggle">
            <input id="messagesVisibleToAllFacilities" type="checkbox" [(ngModel)]="settings.uiSettings.messagesVisibleToAllFacilities" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <div class="settings__row">
          <span class="settings__label-group">
            <label class="settings__label" for="gatewayReferralLinksDisabled">Disable Referral's Pages in GatewAI's menu</label>
            <span class="settings__help" tabindex="0" aria-label="About disabling referral pages in the GatewAI menu"
                  #referralLinksTip="nbTooltip"
                  nbTooltip="On: the Referrals and Referral Analytics links in the GatewAI menu are greyed out and can't be clicked. They stay visible, and the pages still open from a direct link. Off (default): the links work as usual."
                  nbTooltipPlacement="top" nbTooltipClass="settings-tip"
                  (focus)="referralLinksTip.show()" (blur)="referralLinksTip.hide()" (keydown.escape)="referralLinksTip.hide()">?</span>
          </span>
          <label class="toggle">
            <input id="gatewayReferralLinksDisabled" type="checkbox" [(ngModel)]="settings.uiSettings.gatewayReferralLinksDisabled" />
            <span class="toggle__track"></span>
          </label>
        </div>
      </div>

      <div class="settings__card">
        <div class="settings__card-header">
          <span class="settings__icon settings__icon--features"></span>
          <span>Interface</span>
        </div>

        <div class="settings__row">
          <label class="settings__label" for="newJobInfo">New Version: Job Info</label>
          <label class="toggle">
            <input id="newJobInfo" type="checkbox" [(ngModel)]="settings.uiSettings.newVersionPages.jobInfo" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <div class="settings__row">
          <label class="settings__label" for="newReferral">New Version: Referral Response Time</label>
          <label class="toggle">
            <input id="newReferral" type="checkbox" [(ngModel)]="settings.uiSettings.newVersionPages.referralResponseReport" />
            <span class="toggle__track"></span>
          </label>
        </div>

        <div class="settings__divider"></div>

        <!-- A sellable product, not a preference. This screen was super-admin
             only until 2026-09-16, when the owner opened it - this row
             included - to the SNF AI admin (177): our own user inside each
             client, the one who switches paid options on once the client has
             paid. The client's own admin (190) never reaches this page. -->
        <div class="settings__row">
          <label class="settings__label" for="riskProduct">RiskAI (High Risk board)</label>
          <label class="toggle">
            <input id="riskProduct" type="checkbox" [(ngModel)]="settings.uiSettings.products.risk" />
            <span class="toggle__track"></span>
          </label>
        </div>
      </div>

      <div class="settings__footer">
        <button class="settings__save" (click)="save()" [disabled]="!canSave">
          <span *ngIf="!saving">Save Changes</span>
          <span *ngIf="saving" class="settings__save-loading"></span>
        </button>
        <span class="settings__toast" [class.settings__toast--show]="saveSuccess" role="status" aria-live="polite">Changes saved</span>
        <span class="settings__toast settings__toast--info" [class.settings__toast--show]="saveNoChanges" role="status" aria-live="polite">No changes to save</span>
        <span class="settings__toast settings__toast--error" [class.settings__toast--show]="saveError" role="alert" aria-live="assertive">Failed to save. Please try again.</span>
        <span class="settings__toast settings__toast--error" [class.settings__toast--show]="saveConflict" role="alert" aria-live="assertive">Messages visibility was changed elsewhere and has been updated. Review it and save again.</span>
      </div>

    </div>
  </div>
</div>
